import classNames from 'classnames';
import Link from 'next/link';
import { Arrow } from '../SVGs';
import { val } from '../../val.config';

const arrowClass = 'h-[var(--text-2xl)] w-[var(--text-2xl)] pt-2 shrink-0';

type OverlayProps = {
  scheme?: 'light' | 'dark';
  withArrow?: boolean;
  children?: React.ReactNode | React.ReactNode[];
};

const Overlay = ({ scheme, children, withArrow }: OverlayProps) => {
  return (
    <>
      <span
        aria-hidden
        className={classNames(
          'absolute inset-0 -m-2',
          scheme === 'dark' ? 'bg-blank-yellow' : 'bg-blank-black',
          'origin-left scale-x-0 group-hover:scale-x-100',
          'transition-transform duration-1000 ease-out will-change-transform'
        )}
      />
      <span
        aria-hidden
        className={classNames(
          'absolute top-0 left-0',
          'inline-flex items-center w-fit',
          'px-24 py-8 gap-8',
          scheme === 'dark' ? 'text-blank-black' : 'text-blank-yellow',
          '[clip-path:inset(0_100%_0_0)] transition-[clip-path] will-change-transform',
          'duration-900 ease-out group-hover:[clip-path:inset(0_0_0_0)]'
        )}
      >
        {children}
        {withArrow && <Arrow className={arrowClass} />}
      </span>
    </>
  );
};

type ButtonProps = {
  variant?: 'primary' | 'secondary' | 'tertiary';
  scheme?: 'light' | 'dark';
  onClick?: () => void;
  href?: string;
  withArrow?: boolean;
  className?: string;
  children?: React.ReactNode | React.ReactNode[];
  isDisabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  scheme = 'dark',
  onClick,
  href,
  withArrow = false,
  className,
  children,
  isDisabled = false,
  type = 'button',
}) => {
  const baseStyle = classNames(
    'text-xl cursor-pointer',
    'inline-flex items-center w-fit',
    'px-24 py-8 gap-8',
    'border-2',
    'relative group'
  );

  const variantStyle = classNames({
    'bg-[#0000FF] text-blank-yellow border-[#0000FF]': variant === 'primary',

    'bg-[#525646] text-blank-yellow border-[#525646]':
      variant === 'secondary' && scheme === 'dark',
    'bg-[#D4D39A] text-blank-black border-[#D4D39A]':
      variant === 'secondary' && scheme === 'light',

    'bg-blank-black text-blank-yellow border-blank-yellow':
      variant === 'tertiary' && scheme === 'dark',
    'bg-blank-yellow text-blank-black border-blank-black':
      variant === 'tertiary' && scheme === 'light',
  });

  return href ? (
    <Link
      {...val.attrs({ href, children })}
      href={val.raw(href)}
      className={classNames(
        baseStyle,
        variantStyle,
        'no-line-hover-line',
        className
      )}
    >
      {val.raw(children)}
      {withArrow && <Arrow className={arrowClass} />}
      <Overlay scheme={scheme} withArrow={withArrow}>
        {val.raw(children)}
      </Overlay>
    </Link>
  ) : (
    <button
      {...val.attrs({
        children,
      })}
      onClick={onClick}
      className={classNames(baseStyle, variantStyle, className)}
      disabled={isDisabled}
      type={type}
    >
      {val.raw(children)}
      {withArrow && <Arrow className={arrowClass} />}
      <Overlay scheme={scheme} withArrow={withArrow}>
        {val.raw(children)}
      </Overlay>
    </button>
  );
};

export default Button;
